import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FutureSession, FutureSessionService } from './future-session.service';

export interface FutureSessionDialogData {
  sessions: FutureSession[];
}

type RowMode = 'view' | 'correct' | 'delete';

const KNOWN_SPORTS = new Set(['cycling', 'running', 'swimming', 'training', 'walking', 'hiking']);
const KNOWN_ERRORS = new Set([
  'date_in_future', 'date_too_early', 'date_invalid', 'conflict', 'not_future', 'not_found',
]);

/** YYYY-MM-DD aus den ORTS-Komponenten (toISOString verschöbe nach UTC). */
export function toIsoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Einheiten mit Datum in der Zukunft: je Zeile korrigieren oder löschen.
 * Korrigieren schiebt die Einheit auf den echten Tag und lässt sie wieder in
 * die Auswertungen einfließen; Löschen geht über den normalen Löschweg
 * (Einheit samt FIT-Datei).
 */
@Component({
  selector: 'app-future-session-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatDatepickerModule, TranslateModule,
  ],
  template: `
    <h2 mat-dialog-title>{{ 'training_analysis.future_sessions.dialog.title' | translate }}</h2>
    <mat-dialog-content>
      <p class="intro">{{ 'training_analysis.future_sessions.dialog.intro' | translate }}</p>

      @if (!rows.length) {
        <p class="empty">{{ 'training_analysis.future_sessions.dialog.empty' | translate }}</p>
      }

      @for (s of rows; track s.session_id) {
        <div class="fs-row">
          <div class="fs-head">
            @if (showAthlete) {
              <span class="fs-athlete">{{ s.athlete_name || ('#' + s.user_id) }}</span>
            }
            <span class="fs-date">{{ s.date | date: 'dd.MM.yyyy' }}</span>
            <span class="fs-sport">{{ sportLabel(s.sport) }}@if (s.name) { · {{ s.name }} }</span>
            @if (s.imported_at) {
              <span class="fs-meta">
                {{ 'training_analysis.future_sessions.dialog.imported' | translate:{ date: (s.imported_at | date: 'dd.MM.yyyy') } }}
              </span>
            }
          </div>

          @switch (mode[s.session_id] || 'view') {
            @case ('correct') {
              <div class="fs-edit">
                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                  <mat-label>{{ 'training_analysis.future_sessions.dialog.new_date' | translate }}</mat-label>
                  <input matInput [matDatepicker]="picker" [(ngModel)]="newDate[s.session_id]"
                         [min]="minDate" [max]="maxDate" />
                  <mat-datepicker-toggle matIconSuffix [for]="picker" />
                  <mat-datepicker #picker />
                </mat-form-field>
                <button mat-flat-button color="primary" [disabled]="!newDate[s.session_id] || busy[s.session_id]"
                        (click)="saveDate(s)">
                  {{ 'training_analysis.future_sessions.dialog.save' | translate }}
                </button>
                <button mat-button (click)="setMode(s, 'view')">
                  {{ 'training_analysis.future_sessions.dialog.cancel' | translate }}
                </button>
              </div>
            }
            @case ('delete') {
              <div class="fs-edit">
                <span class="fs-confirm">{{ 'training_analysis.future_sessions.dialog.delete_confirm' | translate }}</span>
                <button mat-flat-button color="warn" [disabled]="busy[s.session_id]" (click)="remove(s)">
                  {{ 'training_analysis.future_sessions.dialog.delete_yes' | translate }}
                </button>
                <button mat-button (click)="setMode(s, 'view')">
                  {{ 'training_analysis.future_sessions.dialog.cancel' | translate }}
                </button>
              </div>
            }
            @default {
              <div class="fs-actions">
                <button mat-stroked-button (click)="setMode(s, 'correct')">
                  <mat-icon>event</mat-icon>
                  {{ 'training_analysis.future_sessions.dialog.correct' | translate }}
                </button>
                <button mat-button color="warn" (click)="setMode(s, 'delete')">
                  <mat-icon>delete</mat-icon>
                  {{ 'training_analysis.future_sessions.dialog.delete' | translate }}
                </button>
              </div>
            }
          }

          @if (error[s.session_id]) {
            <div class="fs-error" role="alert">{{ (error[s.session_id] ?? '') | translate }}</div>
          }
        </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ 'training_analysis.future_sessions.dialog.close' | translate }}</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .intro { margin: 0 0 var(--space-12, 12px); color: var(--text-secondary); font-size: var(--fs-sm); }
    .empty { color: var(--text-muted); font-size: var(--fs-sm); }
    .fs-row {
      padding: var(--space-8, 8px) 0;
      border-top: 1px solid var(--border-color);
    }
    .fs-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: var(--space-8, 8px); }
    .fs-athlete { font-weight: 600; }
    .fs-date { font-weight: 600; color: var(--status-warning-text); }
    .fs-sport { color: var(--text-primary); }
    .fs-meta { color: var(--text-muted); font-size: var(--fs-xs); }
    .fs-actions, .fs-edit {
      display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-8, 8px);
      margin-top: var(--space-6, 6px);
    }
    .fs-confirm { font-size: var(--fs-sm); }
    .fs-error { margin-top: var(--space-4, 4px); color: var(--color-error); font-size: var(--fs-sm); }
  `],
})
export class FutureSessionDialogComponent {
  private readonly data = inject<FutureSessionDialogData>(MAT_DIALOG_DATA);
  private readonly svc = inject(FutureSessionService);
  private readonly snack = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  rows: FutureSession[] = [...(this.data?.sessions ?? [])];
  /** Sammelliste eines Coaches: mehr als ein Sportler -> Namen zeigen. */
  readonly showAthlete = new Set(this.rows.map((s) => s.user_id)).size > 1;

  readonly minDate = new Date(1990, 0, 1);
  /** Morgen (Ortszeit) — wie die Regel im Backend: erst danach ist Zukunft. */
  readonly maxDate = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d; })();

  mode: Record<number, RowMode> = {};
  newDate: Record<number, Date | null> = {};
  busy: Record<number, boolean> = {};
  error: Record<number, string | null> = {};

  sportLabel(sport: string | null): string {
    if (!sport) return '';
    return KNOWN_SPORTS.has(sport)
      ? this.translate.instant(`training_analysis.future_sessions.sport.${sport}`)
      : sport;
  }

  setMode(s: FutureSession, mode: RowMode): void {
    this.mode[s.session_id] = mode;
    this.error[s.session_id] = null;
    this.cdr.markForCheck();
  }

  saveDate(s: FutureSession): void {
    const d = this.newDate[s.session_id];
    if (!d) return;
    this.busy[s.session_id] = true;
    this.svc.correctDate(s.session_id, toIsoDate(d)).subscribe({
      next: () => this.done(s, 'training_analysis.future_sessions.dialog.saved'),
      error: (err: unknown) => this.fail(s, err),
    });
  }

  remove(s: FutureSession): void {
    this.busy[s.session_id] = true;
    this.svc.delete(s.session_id).subscribe({
      next: () => this.done(s, 'training_analysis.future_sessions.dialog.deleted'),
      error: (err: unknown) => this.fail(s, err),
    });
  }

  private done(s: FutureSession, messageKey: string): void {
    this.rows = this.rows.filter((r) => r.session_id !== s.session_id);
    delete this.busy[s.session_id];
    this.snack.open(this.translate.instant(messageKey), undefined, { duration: 4000 });
    this.cdr.markForCheck();
  }

  private fail(s: FutureSession, err: unknown): void {
    const code = err instanceof HttpErrorResponse ? err.error?.error : null;
    this.error[s.session_id] = KNOWN_ERRORS.has(code)
      ? `training_analysis.future_sessions.dialog.error.${code}`
      : 'training_analysis.future_sessions.dialog.error.generic';
    this.busy[s.session_id] = false;
    this.cdr.markForCheck();
  }
}
